import { Component, OnInit, OnDestroy, Inject, forwardRef, Renderer2, ChangeDetectorRef, NgZone, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators }                                                                 from '@angular/forms';
import {
	DomSanitizer, SafeHtml
}                                                                                                             from '@angular/platform-browser';
import { ActivatedRoute, Router }                                                                             from '@angular/router';
import { WaitingForResponse }                                                                                 from '@cs/common/utils';
import {
	CsToastManagerService
}                                                                                                             from '@cs/components/toast-manager';
import {
	LoginConfigService, LoginService
}                                                                                                             from '@cs/performance-manager/login';
import {
	AuthenticationService
}                                                                                                             from '@cs/performance-manager/shared';
import { TranslateService }                                                                                   from '@ngx-translate/core';
import { tap }                                                                                                from 'rxjs/operators';

import {
	RequestAccessConfigService
}                                                                                                             from '../request-access-config.service';
import { CsHttpRequestOptions, LoggerUtil, Result }                                                           from '@cs/core';

declare var grecaptcha: any;

@Component({
												standalone: false,
												selector   : 'pmc-request-access-step-one',
												templateUrl: './step-one.component.html'
											})
export class RequestAccessStepOneComponent implements OnInit, OnDestroy {
	username            = '';
	captcha             = '';
	isErrorState        = true;
	identityEstablished = false;
	cancelUrl: string | null = null;

	/**
		* HTML of the Captcha
		*/
	captchaHtml: SafeHtml;

	/**
		* Id of the rendered reCAPTCHA widget. Captured on the first render so a
		* later attempt (e.g. after a rejected request) can reset the existing
		* widget by id — clearing the consumed/expired token — instead of
		* re-rendering the same element, which throws "already rendered" and leaves
		* the challenge in a timed-out state (COH-3297). Null until rendered.
		*/
	private captchaWidgetId: number | null = null;

	/**
		* Indicator for the loader
		*/
	isLoadingCaptcha = true;

	//#region Loaders

	/**
		* Loader flag for the reset password button
		*/
	isWaitingForReset = false;

	//#endregion
	/**
		* Flag indicating to load the captcha
		*/
	useGoogleCaptcha = false;

	private get alreadyInjectedDependency() {
		return document.getElementById('googleCaptcha') != null;
	}

	constructor(private sanitizer: DomSanitizer,
													@Inject(forwardRef(() => LoginService)) private loginService: LoginService,
													@Inject(RequestAccessConfigService) private requestAccessConfigService: RequestAccessConfigService,
													@Inject(forwardRef(() => LoginConfigService)) public loginConfig: LoginConfigService,
													private l8n: TranslateService,
													private toastService: CsToastManagerService,
													private authenticationService: AuthenticationService,
													private renderer: Renderer2,
													private changeRef: ChangeDetectorRef,
													private route: ActivatedRoute,
													private ngzone: NgZone,
													private router: Router,
													private element: ElementRef) {
		window['onloadRecaptchaCallback'] = () => {
			this.refreshCaptcha();
		};

		// Receives the token once the (invisible) challenge completes. The form is
		// already validated in submitWithCaptcha() before the challenge runs.
		window['submitForm'] = (token) => this.ngzone.run(() => {
			this.captcha = token;
			this.requestAccess();
		});

		this.l8n.get('REQUEST_ACCESS')
						.subscribe(value => this.loginService.setTitleMessage(value));
		loginService.setUnderTitleMessage('');

	}

	ngOnInit() {
		this.requestAccessConfigService.initiateRequestAccess()
						.subscribe((result) => {
							this.isLoadingCaptcha     = false;
							const injectedRootUrlHtml = result.value.captchaHtml;
							this.useGoogleCaptcha     = injectedRootUrlHtml
																																			? result.value.requiresGoogleCaptcha || true
																																			: result.value.requiresGoogleCaptcha || false;
							this.captchaHtml          = this.sanitizer.bypassSecurityTrustHtml(injectedRootUrlHtml);
							// New container element — any previous widget id is stale.
							this.captchaWidgetId     = null;

							this.identityEstablished = result.value.identityEstablished || false;
							this.cancelUrl           = result.value.cancelUrl || null;

							if (this.identityEstablished && result.value.loginName) {
								this.username = result.value.loginName;
								// Identity confirmed via SSO — pre-fill the form and let the user confirm.
								this.checkErrorState();
								return;
							}

							this.injectDependency();
						});
	}

	ngOnDestroy(): void {
		this.captchaHtml = '';
		this.changeRef.detectChanges();
	}

	/**
		* Tear down the current reCAPTCHA widget and build a fresh one. Used after a
		* failed attempt instead of grecaptcha.reset(): resetting an invisible widget
		* rejects its internal promises and can leave the next execute() silently
		* dead (COH-3297). Recreating the container via the [innerHtml] binding
		* yields a virgin element, so refreshCaptcha() renders a brand-new widget.
		*/
	private rebuildCaptcha() {
		if (!this.useGoogleCaptcha) return;

		const captchaMarkup = this.captchaHtml;
		this.captchaHtml    = '';
		this.changeRef.detectChanges();
		document.querySelector('.grecaptcha-badge')?.remove();
		this.captchaWidgetId = null;
		this.captchaHtml     = captchaMarkup;
		this.changeRef.detectChanges();
		this.refreshCaptcha();
	}

	refreshCaptcha() {
		setTimeout(() => {
			const container = this.element.nativeElement.querySelector('.request-access .g-recaptcha');
			if (container) {
				try {
					// Rendered-ness is stamped on the container ELEMENT, not inferred from
					// child count — the invisible widget's badge may be re-parented, which
					// would make the container look "empty" and cause a second render on
					// the same element, corrupting grecaptcha's internals (COH-3297).
					if (container.getAttribute('data-cs-rendered') !== 'true') {
						// Fresh (or recreated) container: render our own widget. The script
						// loads with render=explicit, so this is the only render. A badge
						// left behind by a previous widget instance is orphaned — remove it.
						if (this.captchaWidgetId !== null) {
							document.querySelector('.grecaptcha-badge')?.remove();
						}
						// data-bind is stripped so grecaptcha does NOT also bind the submit
						// button — a bound widget goes dead after grecaptcha.reset();
						// submitWithCaptcha() drives execute() explicitly instead.
						container.removeAttribute('data-bind');
						this.captchaWidgetId = grecaptcha.render(container, {
							'sitekey': container.getAttribute('data-sitekey'),
							// The server div declares no data-size; explicit render then
							// defaults to the visible checkbox, so pin the invisible mode
							// this flow is designed for (execute() from submitWithCaptcha).
							'size':    container.getAttribute('data-size') ?? 'invisible',
							'callback': (output) => {
								window['submitForm'](output);
							}
						});
						container.setAttribute('data-cs-rendered', 'true');
					} else if (this.captchaWidgetId !== null) {
						// Live widget (e.g. after a rejected request): clear the consumed/
						// expired token so the next attempt runs a fresh challenge.
						grecaptcha.reset(this.captchaWidgetId);
					}
				} catch (e) {
					LoggerUtil.log(`Failed to render/reset the captcha: ${e?.message ?? e}`);
				}
			}
			this.changeRef.detectChanges();
		}, 0);
		this.changeRef.detectChanges();
	}

	/**
		* Submit entry point for the captcha flow (button click): validate the form
		* first, then programmatically run the invisible challenge. The token arrives
		* in window.submitForm, which performs the actual request.
		*/
	submitWithCaptcha() {
		const form = document.getElementById('form') as HTMLFormElement;
		if (form && !form.checkValidity()) {
			form.reportValidity();
			return;
		}

		if (this.captchaWidgetId !== null) {
			try {
				grecaptcha.execute(this.captchaWidgetId);
			} catch (e) {
				LoggerUtil.log(`Failed to execute the captcha: ${e?.message ?? e}`);
			}
		} else {
			// Widget never rendered (script/network hiccup) — try to recover.
			this.refreshCaptcha();
		}
	}

	/**
		* Send the password request to the server, it contains the captcha and email
		*/
	requestAccess() {
		if (this.identityEstablished) {
			// Identity already confirmed via SSO — skip email verification
			// and go straight to the request wizard.
			this.router.navigate(['sso'], {
				relativeTo: this.route.parent,
				state     : {ssoUsername: this.username}
			});
			return;
		}

		const options: CsHttpRequestOptions = {
			errorResponseHandler: (error): boolean => {
				// Handle documented 400 error
				if (error && (error.status === 401 || error.status === 400)) {
					this.toastService.show({
																													title  : this.l8n.instant('ERROR_SOMETHING_WRONG'),
																													content: error.statusText,
																													type   : 'error'
																												});

					// Rebuild — not grecaptcha.reset(): resetting an invisible widget can
					// leave the next execute() dead (COH-3297).
					this.rebuildCaptcha();
					return true;
				}
			},
			headers             : null
		};

		this.requestAccessConfigService.initRequest(this.username, this.captcha, options)
						.pipe(tap(WaitingForResponse.new(isLoading => this.isWaitingForReset = isLoading)))
						.subscribe((result) => {
							this.router.navigate(['access'], {relativeTo: this.route.parent});
						});
	}

	getErrorMessage() {
		if (this.isLoadingCaptcha) {
			return this.l8n.instant('CAPTCHA_STILL_LOADING');
		} else if (this.username.length === 0) {
			return this.l8n.instant('NO_USERNAME');
		}
	}

	checkErrorState() {
		this.isErrorState = (this.useGoogleCaptcha && this.captcha.length === 0) || this.username.length === 0 || this.isLoadingCaptcha;
	}


	goBackOfLogout() {
		if (this.cancelUrl) {
			// cancelUrl may point outside the Angular base-href (e.g. /login/),
			// so use a full-page navigation instead of the Angular Router.
			window.location.href = this.cancelUrl;
		} else {
			this.authenticationService.logOut();
		}
	}

	private injectDependency() {

		if(!this.useGoogleCaptcha)
			return;

		if (this.alreadyInjectedDependency) {
			this.refreshCaptcha();
			return;
		}

		const script   = document.createElement('script');
		script.id      = 'googleCaptcha';
		// render=explicit: WE render the widget (refreshCaptcha) — auto-render would
		// bind the button via data-bind, and a bound widget breaks after a reset().
		script.src     = 'https://www.google.com/recaptcha/api.js?onload=onloadRecaptchaCallback&render=explicit';
		script.type    = 'text/javascript';
		script.async   = true;
		script.defer   = true;
		script.charset = 'utf-8';
		this.renderer.appendChild(document.head, script);
	}
}
